import * as React from "react";
import {Option, option} from "ts-option";
import {FormBodyProps} from "../../../common/props/FormBodyProps";
import PartnerBrandForm from "../../../partnerBrand/PartnerBrandForm";
import InputFormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/InputFormGroup";
import Row from "../../../layout/Row";
import Col from "../../../layout/Col";
import CurrencyInputFormGroup from "../../../ui/form/CurrencyInputFormGroup";
import RangeNumberInputFormGroup from "../../../ui/form/RangeNumberInputFormGroup";
import CheckboxFormGroup from "../../../ui/form/CheckboxFormGroup";
import AMSelectorFormGroup from "../AMSelectorFormGroup";
import QuoteEmailStyleFormGroup from "../QuoteEmailStyleFormGroup";
import MerchantFormGroup from "../merchant/MerchantFormGroup";
import PreferredMerchantBranchLocationSelectorFormGroup from "../merchant/PreferredMerchantBranchLocationSelectorFormGroup";
import {OptionalFormField} from "../../../../validation/FormField";
import {getLoggedUser} from "../../../../store/view/user";
import {getCurrencySymbol} from "./PartnerBrandSections";

interface ComponentProps {
	/**
	 * Partner brand identifier, empty while the brand is being created.
	 */
	partnerBrandId: Option<string>;
}

type Props = ComponentProps & FormBodyProps<PartnerBrandForm>;

/**
 * Fields nobody fills in today, kept out of the way rather than removed because some of them come back.
 */
export default class PartnerBrandLegacySection extends React.PureComponent<Props> {
	/**
	 * If true, price adjustment factor, automatic payment, gas certificate journey and service reminder are displayed.
	 */
	isAdmin: boolean = getLoggedUser().map(u => u.isAdmin()).getOrElse(false);

	render() {
		const form = this.props.form;
		const currencySymbol = getCurrencySymbol(form);
		return (
			<>
				<Row>
					<Col lg={4} xl={4}>
						<RangeNumberInputFormGroup field={form.markup}
												   minValue={0}
												   maxValue={100}
												   placeholder={"Enter markup"}
												   onChange={this.props.onFieldChange}
												   label={"Markup (%)"}
												   focus={form.hasFocus(form.markup)}
												   maxDecimalPlaces={2}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.labourCost}
												placeholder={"Labour cost"}
												onChange={this.props.onFieldChange}
												label={`Labour cost (${currencySymbol})`}
												focus={form.hasFocus(form.labourCost)}/>
					</Col>
					{this.isAdmin && <Col lg={4} xl={4}>
						<RangeNumberInputFormGroup
							minValue={-100}
							label={"Price Adjustment Factor (%)"}
							placeholder={"Enter price adjustment factor"}
							field={form.priceAdjustmentFactor}
							onChange={this.props.onFieldChange}
							focus={form.hasFocus(form.priceAdjustmentFactor)}
							maxDecimalPlaces={2}/>
					</Col>}
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<RangeNumberInputFormGroup field={form.billDueDays}
												   minValue={0}
												   placeholder={"Enter bill due days number"}
												   onChange={this.props.onFieldChange}
												   label={"Bill due days"}
												   focus={form.hasFocus(form.billDueDays)}
												   maxDecimalPlaces={0}
												   maxValue={10000}/>
					</Col>
					<Col lg={4} xl={4}>
						<RangeNumberInputFormGroup field={form.invoiceDueDays}
												   minValue={0}
												   placeholder={"Enter customer invoice due days number"}
												   onChange={this.props.onFieldChange}
												   label={"Customer invoice due days"}
												   focus={form.hasFocus(form.invoiceDueDays)}
												   maxDecimalPlaces={0}
												   maxValue={10000}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.encourageSms}
										   onChange={this.props.onFieldChange}
										   label={"SMS assurance"}/>
					</Col>
					{this.isAdmin && <Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.serviceReminderAvailable}
										   onChange={this.props.onFieldChange}
										   label={"Service reminder"}/>
					</Col>}
					{this.isAdmin && <Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.automaticPayment}
										   onChange={this.props.onFieldChange}
										   label={"Automatic payment"}/>
					</Col>}
					<Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.showRemittanceOnMobile}
										   onChange={this.props.onFieldChange}
										   label={"Show remittance on mobile"}/>
					</Col>
					<Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.remittanceNotice}
										   onChange={this.props.onFieldChange}
										   label={"Send remittance notice email"}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<AMSelectorFormGroup label={"Account Manager"}
											 onChange={this.props.onFieldChange}
											 field={form.accountManagerId}/>
					</Col>
					<Col lg={4} xl={4}>
						<MerchantFormGroup label={"Preferred merchant"}
										   placeholder={"Select merchant"}
										   field={form.preferredMerchantId}
										   onChange={this.props.onFieldChange}
										   clearable={true}/>
					</Col>
					<Col lg={4} xl={4}>
						<PreferredMerchantBranchLocationSelectorFormGroup label={"Merchant branch"}
																		  placeholder={"Branch location name"}
																		  field={form.preferredMerchantBranchLocationId}
																		  onChange={this.props.onFieldChange}
																		  clearable={true}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<QuoteEmailStyleFormGroup label={"Quote e-mail theme"}
												  placeholder={"Select quote e-mail template theme"}
												  onChange={this.props.onFieldChange}
												  field={form.quoteEmailStyleSuffix}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Zendesk Sell code"
										placeholder="Enter Zendesk Sell Company Code"
										field={form.zendeskSellCompanyCode}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.zendeskSellCompanyCode)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Gas safe number"
										placeholder="Enter Gas safe number"
										field={form.gasNumber}
										onChange={this.onGasNumberChange}
										focus={form.hasFocus(form.gasNumber)}/>
					</Col>
					{this.isAdmin && <Col lg={4} xl={4}>
						<CheckboxFormGroup field={form.gasCertAvailable}
										   onChange={this.props.onFieldChange}
										   label={"Gas certificate journey"}
										   disabled={form.gasNumber.getValue().map(
											   v => v.trim().length == 0).getOrElse(true)}/>
					</Col>}
				</Row>
			</>
		);
	}

	onGasNumberChange = (field: OptionalFormField<string>) => {
		const fieldIsEmpty = field.getValue().map(v => v.trim().length == 0).getOrElse(true);
		if (fieldIsEmpty) {
			this.props.onFieldsChange([field, this.props.form.gasCertAvailable.updateValue(option(false))]);
		}
		else if (this.props.partnerBrandId.isDefined) {
			this.props.onFieldChange(field)
		}
		else {
			this.props.onFieldsChange([field, this.props.form.gasCertAvailable.updateValue(option(true))])
		}
	}
}
